
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Trash2, Plus, GripVertical, Trophy, X, Search, ChevronUp, ChevronDown } from 'lucide-react';
import { useTopGamesAdmin } from '@/hooks/useTopGames';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useDebounce } from '@/hooks/useDebounce';
import { GamePagination } from '@/components/ui/game-pagination';

const ITEMS_PER_PAGE = 10;

const TopGamesManager: React.FC = () => {
  const { topGames, isLoading, addTopGame, removeTopGame, updatePosition, isAdding } = useTopGamesAdmin();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedGameIds, setSelectedGameIds] = useState<string[]>([]);
  const [selectedTopGameIds, setSelectedTopGameIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Fetch available games for selection with enhanced search and filtering
  const { data: availableGames = [], isLoading: isLoadingGames } = useQuery({
    queryKey: ['available-games-for-top', debouncedSearchQuery, categoryFilter],
    queryFn: async () => {
      let query = supabase
        .from('games')
        .select('id, title, thumbnail, category')
        .eq('hidden', false)
        .order('title');

      // Add search filter if query exists - search in both title and category
      if (debouncedSearchQuery.trim()) {
        const searchTerm = debouncedSearchQuery.trim();
        query = query.or(`title.ilike.%${searchTerm}%,category.ilike.%${searchTerm}%`);
      }

      // Add category filter
      if (categoryFilter !== 'all') {
        query = query.eq('category', categoryFilter);
      }

      const { data, error } = await query.limit(100);

      if (error) throw error;
      return data || [];
    },
  });

  // Fetch categories for filter dropdown
  const { data: categories = [] } = useQuery({
    queryKey: ['categories-for-filter'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('name, slug')
        .order('name');

      if (error) throw error;
      return data || [];
    },
  });

  const handleAddSelectedGames = () => {
    if (selectedGameIds.length === 0) {
      toast.error('Please select at least one game');
      return;
    }

    const startPosition = getNextAvailablePosition();
    
    selectedGameIds.forEach((gameId, index) => {
      addTopGame({ 
        gameId, 
        position: startPosition + index 
      });
    });

    setIsAddDialogOpen(false);
    setSelectedGameIds([]);
    setSearchQuery('');
    setCategoryFilter('all');
  };

  const handleBulkRemove = () => {
    if (selectedTopGameIds.length === 0) {
      toast.error('Please select at least one top game to remove');
      return;
    }

    selectedTopGameIds.forEach(topGameId => {
      removeTopGame(topGameId);
    });

    setSelectedTopGameIds([]);
  };

  const getNextAvailablePosition = () => {
    if (!topGames || topGames.length === 0) return 1;
    const maxPosition = Math.max(...topGames.map(tg => tg.position));
    return maxPosition + 1;
  };

  const toggleGameSelection = (gameId: string) => {
    setSelectedGameIds(prev => 
      prev.includes(gameId) 
        ? prev.filter(id => id !== gameId)
        : [...prev, gameId]
    );
  };

  const toggleTopGameSelection = (topGameId: string) => {
    setSelectedTopGameIds(prev =>
      prev.includes(topGameId)
        ? prev.filter(id => id !== topGameId)
        : [...prev, topGameId]
    );
  };

  const selectAllAvailableGames = () => {
    const availableGameIds = availableGamesList
      .map(game => game.id);
    setSelectedGameIds(availableGameIds);
  };

  const clearGameSelection = () => {
    setSelectedGameIds([]);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setCategoryFilter('all');
  };

  const moveUp = (topGameId: string, currentPosition: number) => {
    if (currentPosition <= 1) return;
    updatePosition({ topGameId, newPosition: currentPosition - 1 });
  };

  const moveDown = (topGameId: string, currentPosition: number) => {
    if (!topGames || currentPosition >= topGames.length) return;
    updatePosition({ topGameId, newPosition: currentPosition + 1 });
  };

  const handleDragStart = (e: React.DragEvent, topGameId: string) => {
    setDraggedItem(topGameId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetTopGameId: string) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem === targetTopGameId) return;

    const draggedGame = topGames?.find(tg => tg.id === draggedItem);
    const targetGame = topGames?.find(tg => tg.id === targetTopGameId);

    if (draggedGame && targetGame) {
      // Swap positions
      updatePosition({ topGameId: draggedItem, newPosition: targetGame.position });
      updatePosition({ topGameId: targetTopGameId, newPosition: draggedGame.position });
    }

    setDraggedItem(null);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="w-5 h-5" />
            Top Games Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading top games...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const availableGamesList = availableGames.filter(game => !topGames?.some(tg => tg.game_id === game.id));

  // Pagination for top games list
  const totalPages = Math.ceil((topGames?.length || 0) / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedTopGames = topGames?.slice(startIndex, endIndex) || [];

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <Trophy className="w-5 h-5" />
            Top Games Management
          </CardTitle>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            {selectedTopGameIds.length > 0 && (
              <Button 
                variant="destructive" 
                size="sm"
                onClick={handleBulkRemove}
                className="w-full sm:w-auto"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Remove Selected ({selectedTopGameIds.length})
              </Button>
            )}
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button className="w-full sm:w-auto">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Games
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
                <DialogHeader>
                  <DialogTitle>Add Games to Top Games</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  {/* Enhanced Search and Filter Section */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2 relative">
                      <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Search games by title or category..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 pr-10"
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    
                    <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="Filter by category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {categories.map((category) => (
                          <SelectItem key={category.slug} value={category.slug}>
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Search Results Info */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <Label>
                        {isLoadingGames ? (
                          <span className="flex items-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                            Searching...
                          </span>
                        ) : (
                          `Found ${availableGamesList.length} games (${selectedGameIds.length} selected)`
                        )}
                      </Label>
                      {(searchQuery || categoryFilter !== 'all') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={clearSearch}
                          className="text-xs"
                        >
                          Clear Filters
                        </Button>
                      )}
                    </div>
                    <div className="flex gap-2 w-full sm:w-auto">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={selectAllAvailableGames}
                        disabled={availableGamesList.length === 0 || isLoadingGames}
                        className="flex-1 sm:flex-none"
                      >
                        Select All
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={clearGameSelection}
                        disabled={selectedGameIds.length === 0}
                        className="flex-1 sm:flex-none"
                      >
                        Clear Selection
                      </Button>
                    </div>
                  </div>
                  
                  <div className="max-h-96 overflow-y-auto border rounded-lg p-4">
                    {isLoadingGames ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="mt-2 text-gray-600">Searching games...</p>
                      </div>
                    ) : availableGamesList.length === 0 ? (
                      <div className="text-center text-gray-500 py-8">
                        {debouncedSearchQuery.trim() || categoryFilter !== 'all' ? (
                          <div>
                            <p className="mb-2">No games found matching your search criteria:</p>
                            {debouncedSearchQuery.trim() && (
                              <p className="text-sm">Search: "{debouncedSearchQuery.trim()}"</p>
                            )}
                            {categoryFilter !== 'all' && (
                              <p className="text-sm">Category: {categories.find(c => c.slug === categoryFilter)?.name}</p>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={clearSearch}
                              className="mt-3"
                            >
                              Clear Filters
                            </Button>
                          </div>
                        ) : (
                          'All games are already in top games'
                        )}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-3">
                        {availableGamesList.map((game) => (
                          <div
                            key={game.id}
                            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                              selectedGameIds.includes(game.id) 
                                ? 'bg-blue-50 border-blue-200' 
                                : 'hover:bg-gray-50'
                            }`}
                            onClick={() => toggleGameSelection(game.id)}
                          >
                            <Checkbox
                              checked={selectedGameIds.includes(game.id)}
                              onChange={() => toggleGameSelection(game.id)}
                            />
                            {game.thumbnail && (
                              <img
                                src={game.thumbnail}
                                alt={game.title}
                                className="w-12 h-12 rounded object-cover border flex-shrink-0"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-gray-900 truncate">
                                {game.title}
                              </h4>
                              <p className="text-sm text-gray-600 capitalize">
                                {game.category}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button 
                      onClick={handleAddSelectedGames} 
                      disabled={selectedGameIds.length === 0 || isAdding}
                      className="flex-1"
                    >
                      {isAdding ? 'Adding...' : `Add Selected Games (${selectedGameIds.length})`}
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setIsAddDialogOpen(false);
                        setSearchQuery('');
                        setCategoryFilter('all');
                        setSelectedGameIds([]);
                      }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!topGames || topGames.length === 0 ? (
          <div className="text-center py-8">
            <Trophy className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Top Games Set</h3>
            <p className="text-gray-600 mb-4">Start by adding games to your top games list.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Checkbox
                checked={selectedTopGameIds.length === paginatedTopGames.length && paginatedTopGames.length > 0}
                onCheckedChange={(checked) => {
                  if (checked) {
                    setSelectedTopGameIds(paginatedTopGames.map(tg => tg.id));
                  } else {
                    setSelectedTopGameIds([]);
                  }
                }}
              />
              <Label className="text-sm text-gray-600">
                Select All on Page ({selectedTopGameIds.length}/{topGames.length} total selected)
              </Label>
            </div>

            <div className="space-y-3">
              {paginatedTopGames.map((topGame, index) => (
                <div
                  key={topGame.id}
                  className={`flex items-center gap-2 sm:gap-4 p-3 sm:p-4 border rounded-lg transition-colors ${
                    selectedTopGameIds.includes(topGame.id) 
                      ? 'bg-blue-50 border-blue-200' 
                      : 'hover:bg-gray-50'
                  } ${draggedItem === topGame.id ? 'opacity-50' : ''}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, topGame.id)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, topGame.id)}
                >
                  <Checkbox
                    checked={selectedTopGameIds.includes(topGame.id)}
                    onCheckedChange={() => toggleTopGameSelection(topGame.id)}
                    className="flex-shrink-0"
                  />
                  
                  <div className="flex-shrink-0 cursor-move">
                    <GripVertical className="w-5 h-5 text-gray-400" />
                  </div>
                  
                  <div className="flex-shrink-0">
                    <Badge variant="secondary" className="w-8 h-8 rounded-full flex items-center justify-center text-xs">
                      {topGame.position}
                    </Badge>
                  </div>
                  
                  {topGame.games?.thumbnail && (
                    <img
                      src={topGame.games.thumbnail}
                      alt={topGame.games?.title}
                      className="w-10 h-10 sm:w-12 sm:h-12 rounded object-cover border flex-shrink-0"
                    />
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-900 truncate text-sm sm:text-base">
                      {topGame.games?.title}
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-600 capitalize">
                      {topGame.games?.category}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                    {/* Move Up/Down Buttons */}
                    <div className="flex flex-col gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => moveUp(topGame.id, topGame.position)}
                        disabled={topGame.position <= 1}
                        className="h-6 w-6 sm:h-8 sm:w-8 p-0"
                      >
                        <ChevronUp className="w-3 h-3 sm:w-4 sm:h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => moveDown(topGame.id, topGame.position)}
                        disabled={!topGames || topGame.position >= topGames.length}
                        className="h-6 w-6 sm:h-8 sm:w-8 p-0"
                      >
                        <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4" />
                      </Button>
                    </div>
                    
                    {/* Position Input */}
                    <Input
                      type="number"
                      value={topGame.position}
                      onChange={(e) => {
                        const newPos = parseInt(e.target.value);
                        if (newPos && newPos !== topGame.position) {
                          updatePosition({ topGameId: topGame.id, newPosition: newPos });
                        }
                      }}
                      className="w-12 sm:w-16 h-8 text-xs sm:text-sm"
                      min="1"
                      max={topGames?.length || 1}
                    />
                    
                    {/* Remove Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeTopGame(topGame.id)}
                      className="text-red-600 hover:text-red-700 h-8 w-8 p-0"
                    >
                      <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6">
                <GamePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TopGamesManager;
